<template>
  <div class="tw-bg-gray-100 tw-min-h-screen">
    <div v-if="isLoading"></div>
    <div class="tw-max-w-3xl tw-py-6 tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-mx-auto tw-font-thin" v-else>
      <div class="tw-flex tw-flex-col tw-items-center">
        <Avatar class="tw-mb-3" :height="100" :width="100" :business-id="business.businessId" avatar-type="business" :editable="false" rounded />
      </div>
      <div v-if="business.operatingModel == 'coordinator'" class="tw-flex tw-flex-col tw-items-center tw-px-4">
        <div class="tw-text-lg tw-text-center" v-if="businessProgram">A personalized program has been created for you by {{ business.location }}:</div>
        <div class="tw-text-lg tw-text-center" v-else>Welcome! You've been invited to the following {{ `plan${plans.length > 1 ? "s" : ""}` }} by {{ business.location }}:</div>
        <div class="tw-text-gray-500 tw-text-sm">Managed by {{ business.name }}</div>
      </div>
      <base-card class="tw-mt-6" shadow v-if="!businessProgram">
        <h3 class="tw-mt-1 sm:tw-mt-0 tw-text-lg tw-leading-6 tw-font-normal tw-text-gray-900 tw-mb-3">{{ `Plan${plans.length > 1 ? "s" : ""}` }}</h3>
        <div v-for="plan in plans" :key="plan.businessPlanInviteId" class="tw-flex tw-justify-between tw-border-t tw-py-3">
          <div>{{ plan.name }}</div>
          <div>{{ `$${(plan.priceCents / 100).toFixed(2)}` }}</div>
        </div>
      </base-card>
      <base-card class="tw-mt-6" shadow>
        <h3 class="tw-mt-1 sm:tw-mt-0 tw-text-lg tw-leading-6 tw-font-normal tw-text-gray-900">{{ businessProgram ? "Patient Information" : "Info" }}</h3>
        <base-form ref="infoForm" v-model="isInfoFormValid">
          <div class="tw-mt-4 tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-x-4">
            <base-text-field class="sm:tw-col-span-2" label="Full Name" v-model="user.fullName" required />
            <base-email-field label="Email" v-model="user.emailAddress" required />
            <base-phone-number-input label="Mobile Phone" v-model="user.mobileNumber" :e164.sync="user.phone164" required />
            <base-date-picker class="sm:tw-col-span-2" label="Date of Birth" v-model="user.dob" required />
            <base-text-field class="sm:tw-col-span-2" label="Medication Allergies" placeholder="Enter medication allergies" v-if="businessProgram" v-model="user.knownAllergies" />
          </div>
          <div v-if="business.collectMailingAddress" class="tw-mt-3">
            <div v-if="businessProgram" class="tw-flex tw-items-baseline">
              <h3 class="tw-mt-1 sm:tw-mt-0 tw-text-lg tw-leading-6 tw-font-normal tw-text-gray-900">
                Shipping Address for Medication
              </h3>
              <div class="tw-text-xs tw-text-gray-600 tw-ml-1 tw-font-normal">(PO Box not accepted)</div>
            </div>
            <h3 class="tw-mt-1 sm:tw-mt-0 tw-text-lg tw-leading-6 tw-font-normal tw-text-gray-900" v-else>Mailing Address</h3>
            <div class="tw-mt-4 tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-x-4">
              <base-text-field class="sm:tw-col-span-2" label="Street Address" v-model="user.mailingAddressLine1" required />
              <base-text-field class="tw-mb-5 sm:tw-mb-0" label="Apt, Suite, etc" v-model="user.mailingAddressLine2" />
              <base-text-field label="City" v-model="user.mailingCity" required />
              <base-select label="State" placeholder="Select state" :options="STATE_OPTIONS" label-key="name" v-model="user.mailingState" required />
              <base-text-field label="Zip / Postal Code" v-model="user.mailingPostalCode" required />
            </div>
          </div>
          <div v-else>
            <base-text-field label="Zip / Postal Code" v-model="user.postalCode" required />
          </div>
          <div class="tw-mt-3">
            <h3 class="tw-mt-1 tw-mb-3 sm:tw-mt-0 tw-text-lg tw-leading-6 tw-font-normal tw-text-gray-900">Payment Method</h3>
            <div v-if="card">
              <div
                :class="[
                  'tw-flex tw-flex-nowrap tw-py-2.5 tw-px-4 sm:tw-py-4 sm:tw-px-6 tw-rounded-lg',
                  {
                    'tw-border-primary tw-border-2 tw-transition-colors tw-bg-primary-50 tw-duration-75 tw-ease-in-out tw--m-px': paymentOption === 'cardOnFile',
                  },
                  { 'tw-border': paymentOption !== 'cardOnFile' },
                ]"
              >
                <base-radio class="tw-mr-2" value="cardOnFile" name="paymentOption" v-model="paymentOption"></base-radio>
                <div class="tw-flex tw-justify-between tw-w-full">
                  <div>
                    <div class="tw-text-gray-800 tw-text-sm tw-font-normal">Card on file</div>
                    <div class="tw-text-gray-500 tw-text-sm">Use current card on file</div>
                  </div>
                  <div class="tw-flex tw-text-right">
                    <credit-card-icon class="tw-w-8 tw-h-8 tw-my-auto" :name="card.brand" />
                    <div class="tw-ml-3 tw-text-sm" v-if="card.last4">
                      <div>**** {{ card.last4 }}</div>
                      <div class="tw-text-gray-500 tw-text-xs">Exp {{ expDate(card.expMonth, card.expYear) }}</div>
                    </div>
                    <div class="tw-ml-3 tw-text-gray-500" v-else>
                      No Card
                    </div>
                  </div>
                </div>
              </div>
              <div class="tw-my-4" />
              <div
                :class="[
                  'tw-flex tw-flex-nowrap tw-py-2.5 tw-px-4 sm:tw-py-4 sm:tw-px-6 tw-rounded-lg',
                  {
                    'tw-border-primary tw-border-2 tw-transition-colors tw-bg-primary-50 tw-duration-75 tw-ease-in-out tw--m-px': paymentOption === 'manual',
                  },
                  { 'tw-border': paymentOption !== 'manual' },
                ]"
              >
                <base-radio class="tw-mr-2" value="manual" name="paymentOption" v-model="paymentOption"></base-radio>
                <div class="tw-flex tw-flex-col tw-w-full">
                  <div class="tw-text-gray-800 tw-text-sm tw-font-normal">New card (replaces existing)</div>
                  <base-credit-card-input class="tw-mt-3" ref="creditCardInput" hide-name hide-postal-code />
                </div>
              </div>
            </div>
            <base-credit-card-input ref="creditCardInput" hide-name hide-postal-code v-else />
          </div>
        </base-form>
      </base-card>
      <div v-if="businessProgram">
        <base-card class="tw-mt-6" shadow>
          <h3 class="tw-mt-1 sm:tw-mt-0 tw-text-lg tw-leading-6 tw-font-normal tw-text-gray-900 tw-mb-3">Program</h3>
          <div class="tw-flex tw-justify-between tw-border-t tw-py-3">
            <div>{{ businessProgram.name }}</div>
            <money-format :value="plans.reduce((all, next) => all + next.priceCents, 0)" subunits-value />
          </div>
        </base-card>
        <base-card class="tw-mt-6">
          <h3 class="tw-mt-1 sm:tw-mt-0 tw-text-lg tw-leading-6 tw-font-normal tw-text-gray-900 tw-mb-3">Program Overview & Instructions</h3>
          <div v-if="businessProgram" class="tw-mt-4">
            <div class="tw-relative tw-cursor-pointer hover:tw-opacity-95 tw-transition-all tw-duration-150" @click="showInstructions">
              <img :src="thumbnailImgSrc" class="tw-rounded tw-w-full" />
              <div
                class="tw-absolute tw-h-12 tw-w-12 tw-rounded-full tw-bg-white tw-opacity-40 tw-top-1/2 tw-left-1/2 tw-flex tw-justify-center tw-items-center"
                style="transform: translate(-50%, -50%)"
              >
                <play-icon class="tw-h-8 tw-opacity-100" />
              </div>
            </div>
            <base-form ref="instructionForm" v-model="isInstructionFormValid">
              <base-checkbox-group v-model="acknowledgeProgramVideos" required>
                <base-checkbox v-model="acknowledgeProgramVideos" value="acknowledge" class="tw-mt-4">
                  I acknowledge that I've watched the video above
                </base-checkbox>
              </base-checkbox-group>
            </base-form>
          </div>
        </base-card>
      </div>
      <div class="tw-mt-2.5 tw-text-sm">
        By clicking Accept below, you are agreeing to the
        <base-button type="text" @click="$refs.termsModal.show()">Terms of Service</base-button>.
      </div>
      <div class="tw-flex tw-justify-end tw-mt-4">
        <base-button type="flat" @click="$refs.declineModal.show()" class="tw-mr-2">Decline</base-button>
        <base-button @click="acceptInvite" :disabled="!isFormValid" :loading="isAcceptingInvite">Start Plan</base-button>
      </div>
      <terms-modal ref="termsModal" :plans="plans" :business="business" />
      <decline-modal ref="declineModal" />
      <document-modal ref="documentModal" />
      <global-error />
      <notification-wrapper />
    </div>
  </div>
</template>

<script>
import Avatar from "@/components/Avatar";
import TermsModal from "@/views/memberInvite/components/TermsModal";
import DeclineModal from "@/views/memberInvite/components/DeclineModal";
import CreditCardIcon from "@/components/CreditCardIcon";
import DocumentModal from "@/components/DocumentModal";
import { STATE_OPTIONS } from "@/constants/constants";
import redirect from "@/mixins/redirect";
import { padStart } from "lodash";
import MoneyFormat from "vue-money-format";
import { PlayIcon } from "@/assets/svgs";
import GlobalError from "@/components/v2/GlobalError";
import NotificationWrapper from "@/components/v2/NotificationWrapper.vue";

export default {
  mixins: [redirect],
  components: {
    Avatar,
    TermsModal,
    DeclineModal,
    CreditCardIcon,
    DocumentModal,
    MoneyFormat,
    PlayIcon,
    GlobalError,
    NotificationWrapper,
  },
  data() {
    return {
      STATE_OPTIONS,
      isLoading: false,
      isInfoFormValid: true,
      isInstructionFormValid: true,
      isLoadingToken: false,
      isAcceptingInvite: false,
      user: null,
      business: null,
      plans: [],
      card: null,
      businessProgram: null,
      acknowledgeProgramVideos: [],
      token: null,
      paymentOption: "manual",
      thumbnailImgSrc: null,
    };
  },
  computed: {
    isFormValid() {
      return this.isInfoFormValid && this.isInstructionFormValid;
    },
  },
  created() {
    this.getInvitePlans();
  },
  methods: {
    async getInvitePlans() {
      try {
        this.isLoading = true;
        const response = await this.$axios.get(`/businesses/member-invite/${this.$route.params.businessMemberInviteId}`);
        this.user = response.data.user;
        this.business = response.data.business;
        this.plans = response.data.plans;
        this.card = response.data.card;
        this.businessProgram = response.data.businessProgram;
        this.isLoading = false;
        this.selectedBusinessPlanId = this.plans[0].businessPlanId;
        this.$store.commit("organization/setCobrand", this.business.cobrandId);
        if (this.businessProgram) {
          this.getThumbnailImgSrc();
        }
        if (this.card) {
          this.paymentOption = "cardOnFile";
        }
      } finally {
        this.isLoading = false;
      }
    },
    async getThumbnailImgSrc() {
      if (this.businessProgram.onboardingThumbnailId !== null) {
        const response = await this.$axios.get(`/documents/${this.businessProgram.onboardingThumbnailId}`);
        this.thumbnailImgSrc = response.data.downloadUrl;
      }
    },
    async acceptInvite() {
      if (this.paymentOption === "manual") {
        this.token = await this.$refs.creditCardInput.createToken();
      }
      if (!this.$refs.infoForm.validate() || (this.businessProgram && !this.$refs.instructionForm.validate()) || (this.paymentOption === "manual" && !this.token)) return;

      this.isAcceptingInvite = true;

      this.$axios
        .post(`/businesses/member-invite/${this.$route.params.businessMemberInviteId}`, {
          fullName: this.user.fullName,
          email: this.user.email,
          phone: this.user.phone164,
          postalCode: this.user.postalCode,
          tokenId: this.paymentOption === "manual" ? this.token.id : null,
          useOldPayment: this.paymentOption === "cardOnFile",
          mailingAddressLine1: this.user.mailingAddressLine1,
          mailingAddressLine2: this.user.mailingAddressLine2,
          mailingCity: this.user.mailingCity,
          mailingState: this.user.mailingState,
          mailingPostalCode: this.user.mailingPostalCode,
          knownAllergies: this.user.knownAllergies,
          dob: this.user.dob,
        })
        .then(() => {
          this.EventBus.$emit("GLOBAL_NOTIFY", {
            title: "Plan Activated",
            message: `Congratulations! Your plan was activated.`,
          });
          this.$store.commit("memberInvites/clearMemberInvites");
          this.redirectToHome();
        })
        .catch((err) => {
          this.errorTitle = err.response.data[0].message || `Failed to create plan`;
          this.errorMessage = "We were not able to activate your plan.";
          this.error = err;
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: err.response.data[0].message || "Failed to create plan",
            message: "We were not able to activate your plan.",
            error: err,
          });
        })
        .finally(() => {
          this.isAcceptingInvite = false;
        });
    },
    expDate(expMonth, expYear) {
      if (expMonth && expYear) return `${padStart(expMonth, 2, "0")} / ${expYear.toString().slice(-2)}`;
      else return "unknown";
    },
    showInstructions() {
      this.$refs.documentModal.show({
        documentId: this.businessProgram.onboardingDocumentId,
        extension: "mp4",
      });
    },
  },
};
</script>
